/**
 * Tailwind class strings keyed by order status.
 *
 * Order statuses are not biomarker statuses — `mld-status-low/elevated`
 * refer to biomarker values out of range. Mapping `COMPLETE → status-low`
 * (the prior shape) painted finished orders in the same orange the
 * biomarker UI uses for "low value, action needed", which is misleading.
 *
 * Semantics used here:
 *   - in-flight (transit / process) → info blue
 *   - finished cleanly (complete)   → status-normal (green)
 *   - needs attention (hold / partial) → status-borderline (orange)
 *   - bad outcome (rejected)        → status-high (red/pink)
 *   - neutral (pending / unknown)   → muted / gray
 */
type OrderStatusKey =
    | 'IN_TRANSIT'
    | 'IN_PROCESS'
    | 'COMPLETE'
    | 'PARTIALLY_RESULTED'
    | 'ON_HOLD'
    | 'REJECTED'
    | 'PENDING'
    | 'UNKNOWN';

export const ORDER_STATUS_CLASSES: Record<OrderStatusKey, {text: string; bg: string; border: string}> = {
    IN_TRANSIT: {
        text: 'text-mld-info',
        bg: 'bg-mld-info',
        border: 'border-mld-info',
    },
    IN_PROCESS: {
        text: 'text-mld-info',
        bg: 'bg-mld-info',
        border: 'border-mld-info',
    },
    COMPLETE: {
        text: 'text-mld-status-normal',
        bg: 'bg-mld-status-normal',
        border: 'border-mld-status-normal',
    },
    PARTIALLY_RESULTED: {
        text: 'text-mld-status-borderline',
        bg: 'bg-mld-status-borderline',
        border: 'border-mld-status-borderline',
    },
    ON_HOLD: {
        text: 'text-mld-status-borderline',
        bg: 'bg-mld-status-borderline',
        border: 'border-mld-status-borderline',
    },
    REJECTED: {
        text: 'text-mld-status-high',
        bg: 'bg-mld-status-high',
        border: 'border-mld-status-high',
    },
    PENDING: {
        text: 'text-mld-muted-text',
        bg: 'bg-mld-muted-text',
        border: 'border-mld-muted-text',
    },
    UNKNOWN: {
        text: 'text-gray-400',
        bg: 'bg-gray-100',
        border: 'border-gray-200',
    },
};

export const getStatusClass = (status?: string | null, variant: 'text' | 'bg' | 'border' = 'text'): string => {
    const normalized = normalizeStatusKey(status);
    return ORDER_STATUS_CLASSES[normalized]?.[variant] || 'text-gray-400';
};

function normalizeStatusKey(status?: string | null): OrderStatusKey {
    if (!status) {
        return 'UNKNOWN';
    }

    const value = status.trim().toLowerCase();
    if (!value) {
        return 'UNKNOWN';
    }

    if (value.includes('transit')) {
        return 'IN_TRANSIT';
    }
    if (value.includes('process') || value.includes('progress')) {
        return 'IN_PROCESS';
    }
    if (value.includes('partial')) {
        return 'PARTIALLY_RESULTED';
    }
    if (value.includes('hold')) {
        return 'ON_HOLD';
    }
    if (value.includes('reject')) {
        return 'REJECTED';
    }
    if (value.includes('complete')) {
        return 'COMPLETE';
    }
    if (value.includes('pend') || value.includes('await')) {
        return 'PENDING';
    }

    return 'UNKNOWN';
}
